import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { AutoTradeBacktest } from "@/components/AutoTradeBacktest";
import { toast } from "sonner";
import {
  Bot, ShieldAlert, Power, Download, RefreshCw, CheckCircle2, XCircle,
  AlertTriangle, Loader2, FlaskConical, Zap, Clock,
} from "lucide-react";

const INDICES = ["NIFTY", "SENSEX", "BANKNIFTY"];

const fmtT = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit", timeZone: "Asia/Kolkata" }); }
  catch { return "—"; }
};
const inr = (v) => (v == null ? "—" : `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`);
const REASON = {
  target_50_hit: "50% T1 BOOKED", target_75_hit: "75% T1 BOOKED", target_2_hit: "S2 TARGET BOOKED",
  runner_target_hit: "RUNNER BOOKED",
  counter_target_hit: "S2 TARGET BOOKED", counter_stop: "S2 STOP", sl_hit: "SL HIT", breakeven_stop: "BE STOP",
  cutoff_1pm: "2:30PM CUTOFF", kill_switch: "KILL SWITCH", closed_externally: "CLOSED FROM TERMINAL",
  tp_rupees: "₹ TP BOOKED", risk_stop_rupees: "₹ MAX-RISK STOP",
};

function RuleChips() {
  const rules = [
    "Signals valid only after 09:30 IST",
    "S1 entries expire 30 min after signal",
    "Exit logic: per selected strategy (S1 / S2 / Both)",
    "Both mode: separate S1 & S2 capital pools + lots",
    "Max 3 trades / day (shared across strategies)",
    "₹ max risk: sizes lots + hard ₹ stop on live P&L (optional)",
    "₹ take-profit: books whole trade at ₹ target, S1 & S2 (optional)",
    "Entries till 12:45 · HARD cutoff 14:30 IST",
    "SL re-entry: same index 20-min cooldown + new signal",
    "After any exit: 30-min same-direction cooldown · signals born during the trade are spent",
    "Pyramiding available on Strategy 1",
    "LIMIT entry, cancel if unfilled 3 min",
    "Slippage guard 0.25% from signal entry",
  ];
  return (
    <div className="border border-zinc-800 bg-[#0a0a0a] p-4" data-testid="autotrade-rules-card">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-3">Hard-coded rules</div>
      <div className="flex flex-wrap gap-2">
        {rules.map((r) => (
          <span key={r} className="text-[10px] font-mono px-2 py-1 border border-zinc-800 text-zinc-400">{r}</span>
        ))}
      </div>
    </div>
  );
}

function SetupCard({ status, onSaved }) {
  const cfg = status?.config;
  const [dhan, setDhan] = useState(null);
  const [indices, setIndices] = useState(() => {
    const base = {};
    INDICES.forEach((s) => { base[s] = { enabled: false, lots: 1, lots_s2: 1, pyramiding: false }; });
    return base;
  });
  const [capital, setCapital] = useState("");
  const [capS1, setCapS1] = useState("");
  const [capS2, setCapS2] = useState("");
  const [maxRisk, setMaxRisk] = useState("");
  const [takeProfit, setTakeProfit] = useState("");
  const [strategy, setStrategy] = useState("s1");
  const [busy, setBusy] = useState(false);
  const hydrated = useRef(false);
  const both = strategy === "both";

  useEffect(() => {
    api.get("/dhan/status").then((r) => setDhan(r.data)).catch(() => setDhan({ connected: false }));
  }, []);
  useEffect(() => {
    if (cfg && !hydrated.current) {
      hydrated.current = true;
      setIndices((prev) => {
        const next = { ...prev };
        INDICES.forEach((s) => { if (cfg.indices?.[s]) next[s] = { lots_s2: cfg.indices[s].lots, ...cfg.indices[s] }; });
        return next;
      });
      setCapital(String(cfg.max_capital || ""));
      setCapS1(String(cfg.capital_s1 || ""));
      setCapS2(String(cfg.capital_s2 || ""));
      setMaxRisk(cfg.max_risk_per_trade ? String(cfg.max_risk_per_trade) : "");
      setTakeProfit(cfg.take_profit_per_trade ? String(cfg.take_profit_per_trade) : "");
      setStrategy(cfg.strategy || "s1");
    }
  }, [cfg]);

  const save = async () => {
    if (both) {
      if (!(Number(capS1) > 0) || !(Number(capS2) > 0)) return toast.error("Set capital for BOTH Strategy 1 and Strategy 2.");
    } else if (!capital || Number(capital) <= 0) return toast.error("Set the max capital allocation for today.");
    setBusy(true);
    try {
      await api.post("/auto-trade/setup", {
        indices, mode: cfg?.mode || "paper", strategy,
        max_capital: both ? Number(capS1) + Number(capS2) : Number(capital),
        capital_s1: both ? Number(capS1) : null,
        capital_s2: both ? Number(capS2) : null,
        max_risk_per_trade: Number(maxRisk) > 0 ? Number(maxRisk) : 0,
        take_profit_per_trade: Number(takeProfit) > 0 ? Number(takeProfit) : 0,
      });
      toast.success("Morning setup saved.");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-zinc-800 bg-[#0a0a0a] p-4 space-y-4" data-testid="autotrade-setup-card">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Morning setup · before 09:00</div>
        {dhan === null ? <Loader2 className="h-3.5 w-3.5 animate-spin text-zinc-600" /> : dhan.connected ? (
          <span className="flex items-center gap-1 text-[10px] font-mono text-[#22C55E]" data-testid="dhan-token-ok">
            <CheckCircle2 className="h-3.5 w-3.5" /> DHAN TOKEN VALID · {inr(dhan.available_balance)}
          </span>
        ) : (
          <span className="flex items-center gap-1 text-[10px] font-mono text-red-400" data-testid="dhan-token-bad">
            <XCircle className="h-3.5 w-3.5" /> DHAN TOKEN {dhan.stale_token ? "EXPIRED" : "MISSING"} — authorize in Trade Terminal
          </span>
        )}
      </div>

      <div className="space-y-2">
        {INDICES.map((s) => {
          const row = indices[s];
          return (
            <div key={s} className={`flex flex-wrap items-center gap-3 border px-3 py-2 ${row.enabled ? "border-[#007AFF]/50 bg-[#007AFF]/5" : "border-zinc-800"}`}>
              <label className="flex items-center gap-2 cursor-pointer min-w-[120px]">
                <input type="checkbox" checked={row.enabled}
                       onChange={(e) => setIndices({ ...indices, [s]: { ...row, enabled: e.target.checked } })}
                       className="accent-[#007AFF]" data-testid={`autotrade-index-${s}`} />
                <span className="font-mono text-sm font-bold">{s}</span>
              </label>
              <div className="flex items-center gap-2 text-xs text-zinc-400">
                <span>{both ? <span className="text-[#007AFF]">S1</span> : null} Lots</span>
                <input type="number" min="1" max="50" value={row.lots} disabled={!row.enabled}
                       onChange={(e) => setIndices({ ...indices, [s]: { ...row, lots: Math.max(1, parseInt(e.target.value) || 1) } })}
                       className="w-14 bg-black border border-zinc-800 px-2 py-1 font-mono text-white disabled:opacity-40"
                       data-testid={`autotrade-lots-${s}`} />
              </div>
              {both && (
                <div className="flex items-center gap-2 text-xs text-zinc-400">
                  <span><span className="text-[#A855F7]">S2</span> Lots</span>
                  <input type="number" min="1" max="50" value={row.lots_s2 ?? 1} disabled={!row.enabled}
                         onChange={(e) => setIndices({ ...indices, [s]: { ...row, lots_s2: Math.max(1, parseInt(e.target.value) || 1) } })}
                         className="w-14 bg-black border border-zinc-800 px-2 py-1 font-mono text-white disabled:opacity-40"
                         data-testid={`autotrade-lots-s2-${s}`} />
                </div>
              )}
              <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
                <input type="checkbox" checked={row.pyramiding} disabled={!row.enabled}
                       onChange={(e) => setIndices({ ...indices, [s]: { ...row, pyramiding: e.target.checked } })}
                       className="accent-[#F59E0B]" data-testid={`autotrade-pyramid-${s}`} />
                Pyramiding <span className="text-zinc-600">(+1x @ 50% T1, SL→BE)</span>
              </label>
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1">Strategy</div>
          <div className="inline-flex border border-zinc-800" data-testid="autotrade-strategy-toggle">
            {["s1", "s2", "both"].map((s) => (
              <button key={s} onClick={() => setStrategy(s)}
                      className={`px-4 py-2 text-[10px] font-bold uppercase tracking-widest font-mono transition-colors ${strategy === s ? "bg-[#A855F7] text-white" : "text-zinc-400 hover:text-white"}`}
                      data-testid={`autotrade-strategy-${s}`}>
                {s === "s1" ? "Strategy 1" : s === "s2" ? "Strategy 2" : "Both S1 + S2"}
              </button>
            ))}
          </div>
        </div>
        {both ? (
          <>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#007AFF] font-mono mb-1">S1 capital (₹)</div>
              <input type="number" value={capS1} onChange={(e) => setCapS1(e.target.value)} placeholder="e.g. 60000"
                     className="w-36 bg-black border border-zinc-800 focus:border-[#007AFF] px-3 py-2 font-mono text-sm outline-none"
                     data-testid="autotrade-capital-s1-input" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#A855F7] font-mono mb-1">S2 capital (₹)</div>
              <input type="number" value={capS2} onChange={(e) => setCapS2(e.target.value)} placeholder="e.g. 40000"
                     className="w-36 bg-black border border-zinc-800 focus:border-[#A855F7] px-3 py-2 font-mono text-sm outline-none"
                     data-testid="autotrade-capital-s2-input" />
            </div>
            <div className="text-[10px] font-mono text-zinc-500 pb-2.5" data-testid="autotrade-capital-total">
              Total {inr((Number(capS1) || 0) + (Number(capS2) || 0))} · each strategy deploys only from its own pool · 3 trades/day shared
            </div>
          </>
        ) : (
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1">Max capital allocation (₹)</div>
            <input type="number" value={capital} onChange={(e) => setCapital(e.target.value)} placeholder="e.g. 100000"
                   className="w-40 bg-black border border-zinc-800 focus:border-[#007AFF] px-3 py-2 font-mono text-sm outline-none"
                   data-testid="autotrade-capital-input" />
          </div>
        )}
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1">Max risk / trade (₹) <span className="text-zinc-700">optional</span></div>
          <input type="number" min="0" value={maxRisk} onChange={(e) => setMaxRisk(e.target.value)} placeholder="e.g. 5000"
                 title="Sizes lots so the loss at the stop stays under this, AND exits immediately if live P&L falls to −₹ this amount."
                 className="w-32 bg-black border border-zinc-800 focus:border-[#EF4444] px-3 py-2 font-mono text-sm outline-none"
                 data-testid="autotrade-max-risk-input" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1">Take profit / trade (₹) <span className="text-zinc-700">optional</span></div>
          <input type="number" min="0" value={takeProfit} onChange={(e) => setTakeProfit(e.target.value)} placeholder="e.g. 8000"
                 title="Exits the whole position the moment live P&L reaches this — whichever comes first vs the S1/S2 target."
                 className="w-32 bg-black border border-zinc-800 focus:border-[#22C55E] px-3 py-2 font-mono text-sm outline-none"
                 data-testid="autotrade-take-profit-input" />
        </div>
        <button onClick={save} disabled={busy}
                className="bg-[#007AFF] hover:bg-[#3395FF] disabled:opacity-50 text-white text-xs uppercase tracking-widest font-mono font-bold px-5 py-2.5"
                data-testid="autotrade-save-setup">
          {busy ? "Saving…" : "Save setup"}
        </button>
      </div>
    </div>
  );
}

function EngineBar({ status, refresh }) {
  const eng = status?.engine || {};
  const cfg = status?.config;
  const [busy, setBusy] = useState(false);

  const arm = async (armed) => {
    setBusy(true);
    try {
      await api.post("/auto-trade/arm", { armed });
      toast.success(armed ? "Engine ARMED." : "Engine disarmed.");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const switchMode = async (mode) => {
    if (mode === "live" && !window.confirm("GO LIVE?\n\nReal orders will be placed on your DHAN account per the engine rules. Continue?")) return;
    try {
      await api.post("/auto-trade/mode", { mode });
      toast.success(mode === "live" ? "LIVE MODE — real orders enabled." : "Paper mode — virtual fills only.");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const killSwitch = async () => {
    if (!window.confirm("EMERGENCY STOP?\n\nAll open auto-positions will be squared off and the engine locks until tomorrow.")) return;
    try {
      const r = await api.post("/auto-trade/kill");
      toast.success(`Kill switch executed — ${r.data.closed} position(s) closed.`);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const restartEngine = async () => {
    if (!window.confirm("RESTART ENGINE?\n\nThe kill lock will be cleared and the engine returns DISARMED. Restarting grants a FRESH allowance of 2 more trades on top of those already taken today. Arm it again to resume.")) return;
    try {
      await api.post("/auto-trade/restart");
      toast.success("Kill lock cleared — engine is DISARMED. Arm it when ready.");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Restart failed"); }
  };

  const state = eng.killed ? "KILLED" : eng.armed ? "ARMED" : "DISARMED";
  const stateCls = eng.killed ? "text-red-400 border-red-500/50" : eng.armed ? "text-[#22C55E] border-[#22C55E]/50 animate-pulse" : "text-zinc-400 border-zinc-700";

  return (
    <div className="border border-zinc-800 bg-[#0a0a0a] p-4 flex flex-wrap items-center gap-4" data-testid="autotrade-engine-bar">
      <span className={`px-3 py-1.5 border font-mono text-sm font-bold tracking-widest ${stateCls}`} data-testid="engine-state">{state}</span>
      <span className={`px-2 py-1 border font-mono text-[10px] uppercase tracking-widest flex items-center gap-1 ${eng.mode === "live" ? "border-red-500/50 text-red-400" : "border-[#F59E0B]/50 text-[#F59E0B]"}`} data-testid="engine-mode">
        {eng.mode === "live" ? <Zap className="h-3 w-3" /> : <FlaskConical className="h-3 w-3" />}
        {eng.mode === "live" ? "LIVE ORDERS" : "PAPER MODE"}
      </span>
      <div className="text-[10px] font-mono text-zinc-500 flex items-center gap-1">
        <Clock className="h-3 w-3" /> tick {fmtT(eng.last_tick?.at)} · {eng.last_tick?.note || "—"}
      </div>
      <div className="text-xs font-mono text-zinc-300">
        Trades <span className="text-white font-bold">{eng.trades_today}/{eng.max_trades}</span>
        <span className="mx-2 text-zinc-700">|</span>
        Deployed <span className="text-white">{inr(eng.deployed)}</span>
        <span className="mx-2 text-zinc-700">|</span>
        Left <span className="text-white">{inr(eng.remaining_capital)}</span>
        {eng.pools && (
          <span className="ml-2 text-[10px] text-zinc-500" data-testid="engine-pools">
            (<span className="text-[#007AFF]">S1</span> {inr(eng.pools.s1.remaining)} · <span className="text-[#A855F7]">S2</span> {inr(eng.pools.s2.remaining)})
          </span>
        )}
        <span className="mx-2 text-zinc-700">|</span>
        Day P&L <span className={eng.day_pnl >= 0 ? "text-[#22C55E]" : "text-red-400"} data-testid="engine-day-pnl">{inr(eng.day_pnl)}</span>
      </div>
      <div className="ml-auto flex items-center gap-2">
        {cfg && eng.killed && (
          <button onClick={restartEngine}
                  className="text-[10px] uppercase tracking-widest font-mono font-bold px-4 py-2 bg-[#F59E0B] hover:bg-[#fbbf24] text-black"
                  data-testid="engine-restart-btn">
            <Power className="h-3 w-3 inline mr-1" />RESTART ENGINE
          </button>
        )}
        {cfg && !eng.killed && (
          <button onClick={() => arm(!eng.armed)} disabled={busy}
                  className={`text-[10px] uppercase tracking-widest font-mono font-bold px-4 py-2 border transition-colors ${eng.armed ? "border-zinc-700 text-zinc-300 hover:text-white" : "bg-[#22C55E] border-[#22C55E] text-black hover:bg-[#4ade80]"}`}
                  data-testid="engine-arm-btn">
            <Power className="h-3 w-3 inline mr-1" />{eng.armed ? "Disarm" : "ARM ENGINE"}
          </button>
        )}
        {cfg && (
          <button onClick={() => switchMode(eng.mode === "live" ? "paper" : "live")}
                  className="text-[10px] uppercase tracking-widest font-mono px-3 py-2 border border-zinc-700 text-zinc-300 hover:text-white"
                  data-testid="engine-mode-btn">
            {eng.mode === "live" ? "→ Paper" : "GO LIVE"}
          </button>
        )}
        <button onClick={killSwitch}
                className="text-[10px] uppercase tracking-widest font-mono font-bold px-4 py-2 bg-red-600 hover:bg-red-500 text-white"
                data-testid="engine-kill-btn">
          <ShieldAlert className="h-3 w-3 inline mr-1" />KILL
        </button>
      </div>
    </div>
  );
}

function TradesTable({ trades, title, testid, onSquareOff, tickMap, wsLive }) {
  return (
    <div className="border border-zinc-800 bg-[#0a0a0a]" data-testid={testid}>
      <div className="px-4 py-3 border-b border-zinc-800 text-[10px] uppercase tracking-widest text-zinc-500 font-mono flex items-center gap-2">
        {title}
        {wsLive && (
          <span className="flex items-center gap-1 text-[9px] font-mono text-[#22C55E]" data-testid="autotrade-live-badge">
            <span className="h-1.5 w-1.5 rounded-full bg-[#22C55E] animate-pulse" />TICK LIVE
          </span>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs font-mono">
          <thead>
            <tr className="text-zinc-500 text-[10px] uppercase border-b border-zinc-900">
              {["Date", "Index", "Dir", "Instrument", "Lots", "Entry ₹", "Levels E/SL/T1→Exit", "Exit ₹", "Reason", "P&L ₹", "Mode", ...(onSquareOff ? [""] : [])].map((h, i) => (
                <th key={i} className="px-3 py-2 text-left whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {trades.length === 0 && (
              <tr><td colSpan={onSquareOff ? 12 : 11} className="px-3 py-6 text-center text-zinc-600">No trades recorded yet.</td></tr>
            )}
            {trades.map((t) => {
              const s = t.signal || {}, o = t.option || {};
              const liveTick = tickMap?.[String(o.security_id)];
              const livePremium = t.status === "open" && liveTick != null ? liveTick : t.live_premium;
              const totalUnits = t.fills?.reduce((a, f) => a + f.units, 0) || 0;
              const soldUnits = t.scale_out_units || 0;
              const avgBuy = totalUnits ? t.fills.reduce((a, f) => a + f.premium * f.units, 0) / totalUnits : 0;
              const livePnl = t.status === "open" && liveTick != null && totalUnits
                ? (t.realized_pnl || 0) + (liveTick - avgBuy) * (totalUnits - soldUnits)
                : t.live_pnl;
              const pnl = t.status === "open" ? livePnl : t.gross_pnl;
              return (
                <tr key={t.trade_id} className="border-b border-zinc-900/60 hover:bg-zinc-900/30">
                  <td className="px-3 py-2 whitespace-nowrap text-zinc-400">{t.date_ist}</td>
                  <td className="px-3 py-2 font-bold">{t.symbol}</td>
                  <td className={`px-3 py-2 uppercase ${t.direction === "bullish" ? "text-[#22C55E]" : "text-red-400"}`}>{t.direction === "bullish" ? "CE↑" : "PE↓"}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-zinc-300">{o.trading_symbol || `${o.strike} ${o.type}`}{t.strategy === "s2" ? <span className="ml-1 text-[#A855F7]">S2</span> : <span className="ml-1 text-[#007AFF]">S1</span>}{t.pyramided && <span className="ml-1 text-[#F59E0B]">+PYR</span>}{t.scaled_out && <span className="ml-1 text-[#22C55E]">PART</span>}</td>
                  <td className="px-3 py-2">{t.lots}{t.pyramided ? `+${t.lots}` : ""}×{o.lot_size}</td>
                  <td className="px-3 py-2">{t.entry_premium ?? "—"} <span className="text-zinc-600">{fmtT(t.entry_time)}</span></td>
                  <td className="px-3 py-2 whitespace-nowrap text-zinc-400">{s.entry}/{s.stop_loss}/{s.target_1}→<span className="text-white">{t.strategy === "s2" ? (s.counter_target ?? s.target_2) : (s.exit_level ?? s.runner_target ?? s.target_75)}</span></td>
                  <td className="px-3 py-2">{t.exit_premium ?? (t.status === "open" ? livePremium ?? "…" : "—")}
                    {t.exit_premium_source === "estimated" && <span className="ml-1 text-[9px] text-[#F59E0B]" title="No live option price at exit — delta-estimated" data-testid={`exit-est-${t.trade_id}`}>EST</span>}
                    {t.exit_premium_source === "entry_fallback" && <span className="ml-1 text-[9px] text-[#EF4444]" title="No option price at exit — booked at entry premium; P&L not real" data-testid={`exit-unpriced-${t.trade_id}`}>UNPRICED</span>}
                    <span className="text-zinc-600"> {fmtT(t.exit_time)}</span></td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {t.status === "open"
                      ? <span className="text-[#007AFF] animate-pulse">OPEN</span>
                      : t.status === "waiting_fill"
                        ? <span className="text-[#F59E0B]">FILL WAIT</span>
                        : <span className="text-zinc-400">{REASON[t.exit_reason] || t.exit_reason}</span>}
                  </td>
                  <td className={`px-3 py-2 font-bold ${pnl >= 0 ? "text-[#22C55E]" : "text-red-400"}`}>{pnl != null ? Math.round(pnl).toLocaleString("en-IN") : "—"}</td>
                  <td className="px-3 py-2 uppercase text-[10px]">{t.mode}</td>
                  {onSquareOff && (
                    <td className="px-3 py-2">
                      {(t.status === "open" || t.status === "waiting_fill") && (
                        <button onClick={() => onSquareOff(t)}
                                className="text-[9px] uppercase tracking-widest font-mono font-bold border border-red-500/60 text-red-400 hover:bg-red-600 hover:text-white px-3 py-1.5 transition-colors"
                                data-testid={`squareoff-${t.trade_id}`}>
                          Square off
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DecisionLog({ log, eng }) {
  const tickAt = eng?.last_tick?.at ? Date.parse(eng.last_tick.at) : null;
  const ageSec = tickAt ? Math.round((Date.now() - tickAt) / 1000) : null;
  const note = eng?.last_tick?.note || "";
  const alive = ageSec != null && ageSec < 60 && !note.startsWith("error");
  return (
    <div className="border border-zinc-800 bg-[#0a0a0a]" data-testid="autotrade-decision-log">
      <div className="px-4 py-3 border-b border-zinc-800 flex flex-wrap items-center justify-between gap-2 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
        <span>Decision log · today</span>
        <span className="flex items-center gap-2 normal-case tracking-normal" data-testid="autotrade-heartbeat">
          <span className={`h-1.5 w-1.5 rounded-full ${alive ? "bg-[#22C55E] animate-pulse" : note.startsWith("error") ? "bg-[#EF4444]" : "bg-zinc-600"}`} />
          {tickAt ? (
            <span className={note.startsWith("error") ? "text-[#EF4444]" : alive ? "text-zinc-400" : "text-[#F59E0B]"}>
              Engine {alive ? "alive" : note.startsWith("error") ? "ERROR" : "idle"} · last tick {fmtT(eng.last_tick.at)}{ageSec != null ? ` (${ageSec}s ago)` : ""}{note ? ` · ${note}` : ""}
            </span>
          ) : <span>Engine not started</span>}
        </span>
      </div>
      <div className="px-4 py-1.5 border-b border-zinc-900 text-[10px] text-zinc-600 font-mono">
        Quiet log ≠ dead engine: entries are written only when a decision changes (deduped per candle). Watch the heartbeat above.
      </div>
      <div className="max-h-72 overflow-y-auto divide-y divide-zinc-900/60">
        {(!log || log.length === 0) && <div className="px-4 py-6 text-center text-zinc-600 text-xs font-mono">Engine decisions will appear here.</div>}
        {(log || []).map((l, i) => (
          <div key={i} className="px-4 py-2 flex items-start gap-3 text-xs font-mono">
            <span className="text-zinc-600 whitespace-nowrap">{fmtT(l.ts)}</span>
            {l.level === "error" ? <AlertTriangle className="h-3.5 w-3.5 text-red-400 shrink-0 mt-0.5" />
              : l.level === "warn" ? <AlertTriangle className="h-3.5 w-3.5 text-[#F59E0B] shrink-0 mt-0.5" />
                : <CheckCircle2 className="h-3.5 w-3.5 text-zinc-600 shrink-0 mt-0.5" />}
            <span className="text-zinc-300">
              {l.symbol && <span className="text-white font-bold mr-1">{l.symbol}</span>}
              <span className="text-[#007AFF] mr-1">[{l.event}]</span>{l.detail}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AutoTrade() {
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [status, setStatus] = useState(null);
  const [record, setRecord] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [tickMap, setTickMap] = useState({});
  const [wsLive, setWsLive] = useState(false);
  const wsRef = useRef(null);
  const statusRef = useRef(null);

  const sendSubs = useCallback(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== 1) return;
    const open = (statusRef.current?.trades || []).filter((t) => t.status === "open" || t.status === "waiting_fill");
    ws.send(JSON.stringify({
      type: "subscribe",
      instruments: open.map((t) => ({ segment: t.option?.exchange_segment || "NSE_FNO", security_id: String(t.option?.security_id) })),
    }));
  }, []);

  const refresh = useCallback(() => {
    api.get("/auto-trade/status").then((r) => { setStatus(r.data); statusRef.current = r.data; sendSubs(); }).catch(() => {});
    api.get("/auto-trade/trades?days=30").then((r) => setRecord(r.data.items || [])).catch(() => {});
  }, [sendSubs]);

  // Same live feed as the Trade Terminal — ticks update P&L instantly; polling stays as safety net
  useEffect(() => {
    if (!(user?.is_admin || user?.plan === "pro")) return;
    let closed = false;
    let retry = null;
    const connect = () => {
      if (closed) return;
      const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");
      let ws;
      try { ws = new WebSocket(`${base}/api/ws/terminal`); } catch { return; }
      wsRef.current = ws;
      ws.onopen = () => sendSubs();
      ws.onmessage = (ev) => {
        let m;
        try { m = JSON.parse(ev.data); } catch { return; }
        if (m.type === "tick" && m.segment !== "IDX_I") {
          setTickMap((old) => ({ ...old, [String(m.security_id)]: m.ltp }));
        } else if (m.type === "order") {
          refresh();
        } else if (m.type === "feed_status" || m.type === "hello") {
          setWsLive((m.feed || m.status?.feed) === "live");
        }
      };
      ws.onclose = () => { setWsLive(false); if (!closed) retry = setTimeout(connect, 5000); };
      ws.onerror = () => { try { ws.close(); } catch {} };
    };
    connect();
    return () => { closed = true; clearTimeout(retry); try { wsRef.current?.close(); } catch {} };
  }, [user, sendSubs, refresh]);

  useEffect(() => {
    if (!authLoading && (!user || !(user.is_admin || user.plan === "pro"))) nav("/dashboard");
  }, [authLoading, user, nav]);
  useEffect(() => {
    if (!(user?.is_admin || user?.plan === "pro")) return;
    refresh();
    const id = setInterval(refresh, 5000);
    return () => clearInterval(id);
  }, [user, refresh]);

  const squareOff = async (t) => {
    const inst = t.option?.trading_symbol || t.symbol;
    if (!window.confirm(`Square off ${inst} now?\n\nOnly THIS position exits (${t.mode.toUpperCase()} ${t.status === "waiting_fill" ? "pending order will be cancelled" : "market exit"}). The engine stays ARMED and can still trade per its rules.`)) return;
    try {
      const r = await api.post(`/auto-trade/trades/${t.trade_id}/squareoff`);
      const pnl = r.data?.trade?.gross_pnl;
      toast.success(pnl != null ? `Squared off — P&L ₹${Math.round(pnl).toLocaleString("en-IN")}` : "Position squared off.");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Square-off failed"); }
  };

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const r = await api.get("/auto-trade/trades/export?days=90", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `protradeiq_autotrade_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
    finally { setExporting(false); }
  };

  if (authLoading || !(user?.is_admin || user?.plan === "pro")) return null;
  const today = status?.trades || [];

  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar variant="dashboard" />
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-5" data-testid="autotrade-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Bot className="h-6 w-6 text-[#007AFF]" />
            <div>
              <h1 className="font-heading text-2xl font-black uppercase tracking-tight">Auto-Trade Engine</h1>
              <p className="text-xs text-zinc-500 font-mono">NIFTY · SENSEX · BANKNIFTY — runs on YOUR DHAN account & daily token · rule-locked execution</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={refresh} className="p-2 border border-zinc-800 text-zinc-400 hover:text-white" data-testid="autotrade-refresh">
              <RefreshCw className="h-4 w-4" />
            </button>
            <button onClick={exportXlsx} disabled={exporting}
                    className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-mono border border-zinc-700 px-4 py-2.5 text-zinc-300 hover:text-white disabled:opacity-50"
                    data-testid="autotrade-export-btn">
              <Download className="h-3.5 w-3.5" />{exporting ? "Building…" : "Export track record"}
            </button>
          </div>
        </div>

        <EngineBar status={status} refresh={refresh} />
        <div className="grid lg:grid-cols-2 gap-5">
          <SetupCard status={status} onSaved={refresh} />
          <RuleChips />
        </div>
        <TradesTable trades={today} title="Today's auto trades" testid="autotrade-today-table" onSquareOff={squareOff} tickMap={tickMap} wsLive={wsLive} />
        <AutoTradeBacktest cfg={status?.config} />
        <DecisionLog log={status?.log} eng={status?.engine} />
        <TradesTable trades={record} title="Verifiable track record · last 30 days" testid="autotrade-record-table" />
      </main>
    </div>
  );
}
